import { Injectable, inject } from '@angular/core';
import {
  greaterThanNinetyNine,
  notificationCounterLimit,
} from '@core/constants/notification-panel';
import { SecurityNotificationType } from '@shared/enums/security-notification-type.enum';
import { SecurityStatus } from '@shared/enums/security-status';
import { buildQueryFilter } from '@shared/helpers/build-query-filter';
import { Mapper } from '@shared/helpers/mapper';
import { NotificationPanelResponse } from '@shared/models/notification-panel/notification-panel-response';
import { NotificationPanelSecurity } from '@shared/models/notification-panel/notification-panel-security';
import { Security } from '@shared/models/security';
import { CurrentUserService, HeaderService } from '@shared/services';
import moment from 'moment';
import { QueryOptions } from 'odata-query';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  map,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  GstarClient,
  SecurityNotifictionResponseAuditable,
  SecurityNotifictionResponseAuditableODataWithMetadataResponse,
  SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse,
} from 'src/app/web-api-client';
import { SecurityService } from '../security/security.service';

@Injectable({
  providedIn: 'root',
})
export class NotificationPanelService {
  private readonly gstarClient = inject(GstarClient);
  private readonly securityService = inject(SecurityService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly headerService = inject(HeaderService);

  private reloadPendingApprovalCounterSubj: BehaviorSubject<void> =
    new BehaviorSubject<void>(undefined);

  private reloadApprovedCounterSubj: BehaviorSubject<void> =
    new BehaviorSubject<void>(undefined);

  private readonly pendingApprovalCounterQuery: Partial<
    QueryOptions<Security>
  > = {
    filter: `securityStatus/securityStatusId eq ${SecurityStatus.PendingApproval} and isActive eq true`,
    count: true,
    top: 0,
  };

  private readonly approvedFilter: string = `security/securityStatus/securityStatusId eq ${SecurityStatus.Approved} and securityNotificationType/securityNotificationTypeId eq ${SecurityNotificationType.SecurityApproved}`;

  private readonly approvedSecuritiesCounterQuery: Partial<
    QueryOptions<Security>
  > = {
    filter: `${this.approvedFilter} and isNotified eq false and userId eq '${this.currentUserService.currentUserInfo()?.userId}'`,
    count: true,
    top: 0,
  };

  private readonly approvedSecuritiesQuery: Partial<QueryOptions<Security>> = {
    filter: `${this.approvedFilter} and isNotified eq false and userId eq '${this.currentUserService.currentUserInfo()?.userId}'`,
    orderBy: 'modified asc',
  };

  private readonly pendingApprovalSecuritiesQuery: Partial<
    QueryOptions<Security>
  > = {
    filter: `securityStatus/securityStatusId eq ${SecurityStatus.PendingApproval} and isActive eq true`,
    orderBy: 'modified asc',
  };

  pendingApprovalCounter$: Observable<number> =
    this.reloadPendingApprovalCounterSubj.pipe(
      switchMap(() =>
        this.gstarClient.byclient3(
          undefined,
          this.pendingApprovalCounterQuery?.select?.toString(),
          buildQueryFilter(this.pendingApprovalCounterQuery),
          this.pendingApprovalCounterQuery?.top?.toString(),
          this.pendingApprovalCounterQuery?.skip?.toString(),
          this.pendingApprovalCounterQuery?.orderBy?.toString(),
          this.pendingApprovalCounterQuery?.count?.toString(),
          undefined
        )
      ),
      map(
        ({
          odataCount,
        }: SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse): number => {
          return odataCount !== undefined ? odataCount : 0;
        }
      )
    );

  approvedSecuritiesCounter$: Observable<number> =
    this.reloadApprovedCounterSubj.pipe(
      switchMap(() =>
        this.gstarClient.securitynotificationsGET(
          this.approvedSecuritiesCounterQuery?.select?.toString(),
          buildQueryFilter(this.approvedSecuritiesCounterQuery),
          this.approvedSecuritiesCounterQuery?.top?.toString(),
          this.approvedSecuritiesCounterQuery?.skip?.toString(),
          this.approvedSecuritiesCounterQuery?.orderBy?.toString(),
          this.approvedSecuritiesCounterQuery?.count?.toString(),
          undefined
        )
      ),
      map(
        ({
          odataCount,
        }: SecurityNotifictionResponseAuditableODataWithMetadataResponse): number => {
          return odataCount !== undefined ? odataCount : 0;
        }
      ),
      shareReplay(1)
    );

  approvedSecurities$: Observable<NotificationPanelSecurity[]> =
    this.reloadApprovedCounterSubj.pipe(
      switchMap(() =>
        this.gstarClient.securitynotificationsGET(
          this.approvedSecuritiesQuery?.select?.toString(),
          buildQueryFilter(this.approvedSecuritiesQuery),
          this.approvedSecuritiesQuery?.top?.toString(),
          this.approvedSecuritiesQuery?.skip?.toString(),
          this.approvedSecuritiesQuery?.orderBy?.toString(),
          this.approvedSecuritiesQuery?.count?.toString(),
          undefined
        )
      ),
      map(
        (
          securitiesNotificationResponse: SecurityResponseWithNestedObjectsAuditableODataWithMetadataResponse
        ) => {
          return securitiesNotificationResponse.value
            ? this.mapToNotificationPanelSecurity(
                securitiesNotificationResponse.value
              )
            : [];
        }
      )
    );

  pendingApprovalSecurities$: Observable<NotificationPanelSecurity[]> =
    this.reloadPendingApprovalCounterSubj.pipe(
      switchMap(() =>
        this.securityService.query(this.pendingApprovalSecuritiesQuery)
      ),
      map((securities: Security[]): NotificationPanelSecurity[] => {
        return Mapper.mapByList(securities, NotificationPanelSecurity, [
          {
            clientName: (source: Security): string => source.entity.client.name,
            securityId: (source: Security): number => source.securityId,
            securityStatus: source => source.securityStatus.securityStatusId,
            entityTypeName: (source: Security): string =>
              source.entity.entityType.name,
            securityTypeName: (source: Security): string =>
              source.securityType.name,
            investmentJurisdictionName: (source: Security): string =>
              source.investmentJurisdiction.country.name,
            lastUpdated: (source: Security): string =>
              moment
                .duration(moment(new Date()).diff(moment(source.modified)))
                .humanize(),
          },
        ]);
      })
    );

  totalNotificationCounter$: Observable<number> = combineLatest([
    this.pendingApprovalCounter$,
    this.approvedSecuritiesCounter$,
  ]).pipe(map(([pending, approved]): number => pending + approved));

  pendingApprovalSecuritiesPanel$: Observable<NotificationPanelResponse> =
    combineLatest([
      this.pendingApprovalCounter$,
      this.pendingApprovalSecurities$,
    ]).pipe(
      map(([pendingApprovalCounter, pendingApprovalSecurities]) => {
        return Mapper.map({}, NotificationPanelResponse, [
          {
            count: pendingApprovalCounter,
            securities: pendingApprovalSecurities,
          },
        ]);
      })
    );

  approvedSecuritiesPanel$: Observable<NotificationPanelResponse> =
    combineLatest([
      this.approvedSecuritiesCounter$,
      this.approvedSecurities$,
    ]).pipe(
      map(([approvedSecuritiesCounter, approvedSecurities]) => {
        return Mapper.map({}, NotificationPanelResponse, [
          {
            count: approvedSecuritiesCounter,
            securities: approvedSecurities,
          },
        ]);
      })
    );

  notifyApprovedSecurity(securitiesIds: number[]): Observable<void> {
    return this.gstarClient
      .notified(securitiesIds)
      .pipe(tap(() => this.reloadApprovedCounter()));
  }

  reloadPendingApprovalCounter = (): void =>
    this.reloadPendingApprovalCounterSubj.next();

  reloadApprovedCounter = (): void => this.reloadApprovedCounterSubj.next();

  getNotificationCountString(count: number): string {
    return (
      count > notificationCounterLimit ? greaterThanNinetyNine : count
    ).toString();
  }

  private mapToNotificationPanelSecurity(
    securities: SecurityNotifictionResponseAuditable[]
  ): NotificationPanelSecurity[] {
    return Mapper.mapByList(securities, NotificationPanelSecurity, [
      {
        clientName: (
          source: SecurityNotifictionResponseAuditable
        ): string | undefined => source.security?.entity?.client?.name,
        entityTypeName: (
          source: SecurityNotifictionResponseAuditable
        ): string | undefined => source.security?.entity?.entityType?.name,
        investmentJurisdictionName: (
          source: SecurityNotifictionResponseAuditable
        ): string | undefined =>
          source.security?.investmentJurisdiction?.country?.name,
        securityTypeName: (
          source: SecurityNotifictionResponseAuditable
        ): string | undefined => source.security?.securityType?.name,
        securityStatus: (
          source: SecurityNotifictionResponseAuditable
        ): string | undefined => source.security?.securityStatus?.name,
        securityId: (
          source: SecurityNotifictionResponseAuditable
        ): number | undefined => source.security?.securityId,
        lastUpdated: (source: Security): string =>
          moment
            .duration(moment(new Date()).diff(moment(source.modified)))
            .humanize(),
      },
    ]);
  }
}
